import React from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import ShowMoreComponent from '@Components/Results/utils/show-more.component';
import SubstanceQuantitativeTable from '@Components/Results/substances/tables/quantitative/substance-quantitative-table.component';
import TableColumnsSwitcherComponent from '@Components/Results/reactions/reaction-details/table-columns-switcher/table-columns-switcher.component';
import SubstanceQuantitativeReport from '@Components/Results/substances/tables/quantitative/substance-quantitative-report.component';
import SubstanceData from '@Services/SubstanceData';
import { HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME } from '@Pages/Heatmap/HeatmapCellDetailsDialog/constants';

import './substance-quantitative-data.custom.scss';

export default class SubstanceQuantitativeData extends React.PureComponent {
  constructor(props) {
    super(props);

    const { data, uniqueId } = props;

    if (data.headers && data.rows) {
      this.isReport = data.layout === 'list';

      if (!this.isReport) {
        Spinners.activate(this.id);
      }

      this.state = {
        data: this.processData(data),
        headersReady: false,
      };
    } else {
      Logger.error(`SubstanceQuantitativeData: quantitative data is malformed.`, data);
      this.state = {
        error: true,
      };
    }

    this.cache = null;
    this.id = `${uniqueId}-quantitative`;
    this.containerRef = React.createRef();
    this.columnSwitcherRef = React.createRef();

    this.updateHeaders = this.updateHeaders.bind(this);
    this.showAllNonEmptyCols = this.showAllNonEmptyCols.bind(this);
  }

  processData(data) {
    if (this.isReport) {
      return {
        headers: data.headers,
        rows: SubstanceData.buildReportData(data.headers, data.rows),
      };
    }
    return SubstanceData.alterDataShownQuan(data);
  }

  updateHeaders(headers) {
    const { headersReady, data } = this.state;
    if (!headersReady) Spinners.terminate(this.id);
    this.setState({
      data: {
        ...data,
        headers,
      },
      headersReady: true,
    });
  }

  showAllNonEmptyCols() {
    this.columnSwitcherRef.current.showAllNonEmptyCols();
  }

  // eslint-disable-next-line react/destructuring-assignment
  showMore(firstItem = 1, lastItem = this.props.initialItemsAmount) {
    return new Promise((resolve, reject) => {
      if (this.cache) {
        this.appendRows(this.cache);
        this.cache = null;
        resolve();
        return;
      }
      const { retrieveData, track } = this.props;

      Spinners.activate(this.id);

      retrieveData(firstItem, lastItem)
        .then(({ rows }) => {
          this.appendRows(rows);
          if (track) track(rows, 'showMoreQuan', null, firstItem, lastItem);
          return rows;
        })
        .catch(() => reject(new Error('No quantitative data found')))
        .finally(() => {
          Spinners.terminate(this.id);
          return resolve();
        });
    });
  }

  appendRows(rows) {
    // eslint-disable-next-line react/destructuring-assignment
    const data = { ...this.state.data };
    data.rows = data.rows.concat(rows);
    this.setState({ data: this.processData(data) });
  }

  showLess() {
    // eslint-disable-next-line react/destructuring-assignment,react/no-access-state-in-setstate
    const data = { ...this.state.data };
    // eslint-disable-next-line react/destructuring-assignment
    this.cache = data.rows.splice(this.props.initialItemsAmount);
    data.rows = [...data.rows];
    this.setState({ data });
  }

  render() {
    const { headersReady, data, error } = this.state;
    const { metadata, total, showTitle, initialItemsAmount, columnsViewedByDefault } = this.props;
    const scrollContainerSelector = metadata.heatmapMode ? `.${HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME}` : null;

    if (error) {
      return <div className="substance-results-quantitative-error">Information currently unavailable.</div>;
    }

    return (
      <div className="substance-results-quantitative" id={this.id} ref={this.containerRef}>
        {showTitle && (
          <div className="typography-small">
            <strong>Quantitative Results</strong>
          </div>
        )}

        {!this.isReport && (
          <>
            <div className="rx-btn-group--right">
              <TableColumnsSwitcherComponent
                headers={data && data.headers}
                uniqueId={this.id}
                containerRef={this.containerRef}
                ref={this.columnSwitcherRef}
                updateHeaders={this.updateHeaders}
                defaultColumns={columnsViewedByDefault}
                scrollable={metadata.heatmapMode}
              />
            </div>
            {headersReady && (
              <SubstanceQuantitativeTable
                metadata={metadata}
                data={data}
                uniqueId={this.id}
                showAllNonEmptyCols={this.showAllNonEmptyCols}
              />
            )}
          </>
        )}
        {this.isReport && <SubstanceQuantitativeReport metadata={metadata} data={data.rows} total={total} />}

        <RxSpinnerComponent spinnerId={this.id} position="relative" />

        <div className="rx-btn-group--center">
          <ShowMoreComponent
            initial={initialItemsAmount}
            scrollContainerSelector={scrollContainerSelector}
            metadata={metadata}
            current={data && data.rows && data.rows.length}
            total={total}
            container={`#${this.id}`}
            moreHandler={({ firstItem, lastItem }) => this.showMore(firstItem, lastItem)}
            lessHandler={() => this.showLess()}
          />
        </div>
      </div>
    );
  }
}

SubstanceQuantitativeData.defaultProps = {
  initialItemsAmount: 7,
};

SubstanceQuantitativeData.propTypes = {
  metadata: MetadataPropTypesShape,
  total: PropTypes.number.isRequired,
  data: PropTypes.shape({
    headers: PropTypes.array,
    rows: PropTypes.array,
    layout: PropTypes.string,
  }).isRequired,
  uniqueId: PropTypes.string.isRequired,
  retrieveData: PropTypes.func.isRequired,
  showTitle: PropTypes.bool,
  initialItemsAmount: PropTypes.number,
  columnsViewedByDefault: PropTypes.array,
  // eslint-disable-next-line react/no-unused-prop-types
  substanceId: PropTypes.string.isRequired,
  track: PropTypes.func,
};
